import { Grid } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import { FieldValues, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "../../../app/hooks";
import DateField from "../../../common/components/functional/dateField/DateField";
import InputHelperText from "../../../common/components/functional/error/InputHelperText";
import CustomerAutocompleteField from "../../../common/components/functional/fields/customerAutocompleteField/CustomerAutocompleteField";
import { CustomerOption } from "../../../common/components/functional/fields/customerAutocompleteField/useCustomerAutocomplete";
import CustomerCostCenterAutocompleteField from "../../../common/components/functional/fields/customerCostCenterAutocompleteField/CustomerCostCenterAutocompleteField";
import DateWithIndefiniteField from "../../../common/components/functional/fields/dateWithIndefiniteField/DateWithIndefiniteField";
import OrderRowTemplateAutocompleteField from "../../../common/components/functional/fields/orderRowTemplateAutocompleteField/OrderRowTemplateAutocompleteField";
import TempEmploymentReasonField from "../../../common/components/functional/fields/tempEmploymentReasonField/TempEmploymentReasonField";
import { WorkLocationOption } from "../../../common/components/functional/fields/workLocationAutocompleteField/useWorkLocationAutocomplete";
import WorkLocationAutocompleteField from "../../../common/components/functional/fields/workLocationAutocompleteField/WorkLocationAutocompleteField";
import FormGridRow from "../../../common/components/functional/formGridRow/FormGridRow";
import { EXCLUSIVITIES_PAGE_SIZE } from "../../../common/components/functional/pagination/constants";
import { TalentBlacklist, TalentExclusivity } from "../../talents/talentAPI";
import {
  fetchExclusivitiesByTalentId,
  getBlacklistByTalentId,
} from "../../talents/talentsSlice";
import {
  validateEndDate,
  validateIndefinite,
  validateStartDate,
} from "./helpers";
import { TalentSelfServiceOrderPermissionForm } from "./types";

type Props = {
  customerOrganization: CustomerOption | null;
  workLocation: WorkLocationOption | null;
  resetOnCustomerOrganizationChange: (org: CustomerOption | null) => void;
  resetOnWorkLocationChange: (wl: WorkLocationOption | null) => void;
  isEditing: boolean;
  isTalentActive: boolean;
};

const SelfServicePermissionForm = ({
  customerOrganization,
  workLocation,
  resetOnCustomerOrganizationChange,
  resetOnWorkLocationChange,
  isEditing,
  isTalentActive,
}: Props) => {
  const [blacklists, setBlacklists] = useState<TalentBlacklist[]>([]);
  const [exclusivities, setExclusivities] = useState<TalentExclusivity[]>([]);

  const { t } = useTranslation();
  const { talentId } = useParams();
  const dispatch = useAppDispatch();

  const {
    formState: { errors },
    watch,
  } = useFormContext<TalentSelfServiceOrderPermissionForm>();

  const orderRowTemplate = watch("orderRowTemplate");
  const workLocationValue = watch("workLocation");

  useEffect(() => {
    if (!talentId) return;
    dispatch(getBlacklistByTalentId(talentId)).then((action) => {
      if (isFulfilled(action)) {
        setBlacklists(action.payload);
      }
    });
    dispatch(
      fetchExclusivitiesByTalentId({
        talentId,
        pageSize: EXCLUSIVITIES_PAGE_SIZE,
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        setExclusivities(action.payload.data);
      }
    });
  }, [dispatch, talentId]);

  const poolProConflict = useMemo(() => {
    if (!workLocationValue?.id || !orderRowTemplate?.workTitleId) return false;

    return (
      exclusivities.length > 0 &&
      !exclusivities.some((ex) => {
        if (
          ex.workLocationId &&
          ex.workLocationId !== workLocationValue.id
        ) {
          return false;
        }
        if (
          ex.customerOrganizationId &&
          ex.customerOrganizationId !== workLocationValue.customerOrgId
        ) {
          return false;
        }
        if (
          ex.customerCompanyId &&
          customerOrganization?.customerCompanyId &&
          ex.customerCompanyId !== customerOrganization.customerCompanyId
        ) {
          return false;
        }

        const workTitles = ex.workTitles || [];
        if (workTitles.length === 0) return true;
        return workTitles.some((wt) => wt.id === orderRowTemplate.workTitleId);
      })
    );
  }, [exclusivities, workLocationValue, customerOrganization, orderRowTemplate]);

  const blacklistConflict = useMemo(() => {
    if (
      !customerOrganization?.id ||
      !workLocationValue?.id ||
      !orderRowTemplate?.workTitleId
    ) {
      return false;
    }

    return blacklists.some((entry: TalentBlacklist) => {
      if (
        entry.customerCompanyId &&
        entry.customerCompanyId !== customerOrganization.customerCompanyId
      ) {
        return false;
      }

      if (
        entry.customerOrganizationId &&
        entry.customerOrganizationId !== customerOrganization.id
      ) {
        return false;
      }

      if (
        entry.workLocationId &&
        entry.workLocationId !== workLocationValue.id
      ) {
        return false;
      }

      const workTitles = entry.workTitles || [];
      if (workTitles.length === 0) {
        return true;
      }

      return workTitles.some(
        (title: { id: string }) => title.id === orderRowTemplate.workTitleId,
      );
    });
  }, [blacklists, customerOrganization, orderRowTemplate, workLocationValue]);

  return (
    <Grid container alignItems="center" columnSpacing={2} rowSpacing={1}>
      <FormGridRow
        label={t("talentProfile:organization")}
        labelSize={3}
        fieldSize={7}
      >
        <CustomerAutocompleteField
          name="customerOrganization"
          required
          fullWidth
          includeCompanies
          onChangeOverride={resetOnCustomerOrganizationChange}
          error={!!errors.customerOrganization}
          disabled={isEditing}
        />
      </FormGridRow>
      <FormGridRow label={t("order:workLocation")} labelSize={3} fieldSize={7}>
        <WorkLocationAutocompleteField
          name="workLocation"
          fullWidth
          required
          customerCompanyId={customerOrganization?.customerCompanyId}
          customerOrgId={customerOrganization?.id}
          disabled={!customerOrganization?.id || isEditing}
          onChangeOverride={resetOnWorkLocationChange}
          error={!!errors.workLocation}
        />
      </FormGridRow>
      <FormGridRow label={t("order:orderTemplate")} labelSize={3} fieldSize={7}>
        <OrderRowTemplateAutocompleteField
          name="orderRowTemplate"
          fullWidth
          required
          contractId={workLocation?.contractId}
          disabled={!workLocation?.contractId || isEditing}
          error={!!errors.orderRowTemplate}
        />
      </FormGridRow>
      <FormGridRow
        label={t("order:customerCostCenter")}
        labelSize={3}
        fieldSize={7}
      >
        <CustomerCostCenterAutocompleteField
          name="customerCostCenter"
          fullWidth
          required
          workLocationId={workLocation?.id}
          disabled={!customerOrganization?.customerCompanyId}
          error={!!errors.customerCostCenter}
        />
      </FormGridRow>
      <FormGridRow
        label={t("talentProfile:reasonForTemporaryEmployment")}
        labelSize={3}
        fieldSize={7}
      >
        <TempEmploymentReasonField
          name="reasonCode"
          fullWidth
          required
          error={!!errors.reasonCode}
        />
      </FormGridRow>
      <FormGridRow label={t("talentProfile:from")} labelSize={3} fieldSize={7}>
        <DateField
          name="permissionStart"
          required={{
            value: true,
            message: "required_permissionStart",
          }}
          validate={(value: Date | null, fieldValues?: FieldValues) =>
            validateStartDate(value, fieldValues?.permissionEnd)
          }
          error={!!errors.permissionStart}
          sx={{ maxWidth: "183px" }}
        />
      </FormGridRow>
      <FormGridRow
        multiRow
        label={t("talentProfile:to")}
        labelSize={3}
        fieldSize={7}
      >
        <DateWithIndefiniteField
          dateName="permissionEnd"
          indefiniteFieldName="indefinite"
          sx={{ maxWidth: "183px" }}
          validate={(value: Date | null, fieldValues?: FieldValues) =>
            validateEndDate(
              value,
              fieldValues?.indefinite,
              fieldValues?.permissionStart,
            )
          }
          indefiniteRules={{
            validate: validateIndefinite,
          }}
          error={!!errors.permissionEnd}
        />
      </FormGridRow>
      {blacklistConflict && (
        <InputHelperText
          errorMessage={t(
            "talentProfile:selfServicePermissionBlacklistConflict",
          )}
        />
      )}
      {poolProConflict && (
        <InputHelperText
          errorMessage={t("talentProfile:selfServicePermissionPoolProConflict")}
        />
      )}
      {!isTalentActive && (
        <InputHelperText errorMessage={t("errors:talent_must_be_active")} />
      )}
    </Grid>
  );
};

export default SelfServicePermissionForm;
